/**
 * Game Service
 * Consolidated service for game plays, user interactions, and game-related operations
 */

import { apiClient } from './apiClient';
import { getPlayDate } from '../utils/dateUtils';
import { ensureToken, getToken } from './tokenService';

/**
 * Update or create a game play record
 *
 * The client always supplies `play_date` - only the browser knows the user's
 * local date, and the server no longer guesses one (roadmap D23).
 *
 * Scoring no longer requires an account: `ensureToken` creates a guest record
 * on the spot if this is the visitor's first write (D22).
 *
 * @param {Object} playData - The play data
 * @param {string} playData.gameId - The game ID
 * @param {string} playData.score - The score (optional)
 * @param {string} playData.message - The message (optional)
 * @param {string} playData.play_date - The play date in YYYY-MM-DD format (optional, defaults to today on this device)
 * @returns {Promise<Object>} The saved play
 */
export const updateGamePlay = async (playData) => {
  try {
    await ensureToken();
    return await apiClient.post('/plays/update', {
      play_date: getPlayDate(),
      ...playData
    });
  } catch (error) {
    console.error('Error updating game play:', error);
    throw error;
  }
};

/**
 * Create an empty score record for a game before navigation
 *
 * This is also what marks a game as played today: "played" is now derived
 * from a `plays` row rather than tracked in localStorage (D22), so every
 * game-link click writes one, score or no score.
 *
 * @param {string} gameId - The game ID
 * @returns {Promise<Object>} Response with id property
 */
export const createEmptyScoreRecord = async (gameId) => {
  try {
    await ensureToken();
    const response = await apiClient.post('/plays/update', {
      gameId: gameId,
      play_date: getPlayDate(),
      // Marks this as a click-through rather than a score submission: the
      // server counts these, and only these, toward the auto-score threshold.
      // Sending no `score` is also what tells it to create the row without
      // overwriting a score already logged for this game today.
      game_opened: true
    });
    return response;
  } catch (error) {
    console.error('Error creating empty score record:', error);
    throw error;
  }
};

/**
 * Get friends dashboard data (user, friends, today's plays, streak)
 *
 * "Today" is passed in: the server can't know the caller's timezone.
 * Returns null without a round trip when there's no token - a visitor who
 * has never written anything has no record to read.
 *
 * @returns {Promise<Object|null>} Dashboard data with user, friends, today_plays and streak
 */
export const getFriendsDashboard = async () => {
  if (!getToken()) return null;

  try {
    const response = await apiClient.get(`/friends/dashboard?date=${getPlayDate()}`);
    return response;
  } catch (error) {
    console.error('Error fetching friends dashboard:', error);
    throw error;
  }
};

/**
 * Add a friend by username
 * @param {string} username - The username to add as friend
 * @returns {Promise<void>} Success confirmation
 */
export const addFriend = async (username) => {
  try {
    await ensureToken();
    await apiClient.post('/friends/add', { username });
  } catch (error) {
    console.error('Error adding friend:', error);
    throw error;
  }
};

/**
 * Remove a friend
 * @param {number} friendId - The friend ID to remove
 * @returns {Promise<void>} Success confirmation
 */
export const removeFriend = async (friendId) => {
  try {
    await apiClient.delete(`/friends/${friendId}/remove`);
  } catch (error) {
    console.error('Error removing friend:', error);
    throw error;
  }
};
